import { apiClient } from './api'
import { clearToken, setToken } from './authToken'

/**
 * Attendee magic-link auth client. On the web the session is an HttpOnly cookie
 * set by the API on verify (axios sends it via `withCredentials: true`). On
 * native, verify also returns the JWT in the body, which we persist to secure
 * storage and send as `Authorization: Bearer` (see services/authToken + api).
 */

export interface AttendeeMe {
  authenticated: boolean
  id?: string
  eventId?: string
  eventSlug?: string
  email?: string
  fullName?: string
  company?: string
  track?: string
  needsOnboarding?: boolean
}

export interface VerifyResult {
  id: string
  eventId: string
  email: string
  fullName?: string
  token?: string
  expiresIn?: number
}

export const attendeeAuth = {
  /** Request a magic link for an event (always 202; no enumeration leak). */
  requestLink: (email: string, eventSlug: string) =>
    apiClient
      .post('/attendee/request-link', { email, eventSlug })
      .then((r) => r.data),

  /** Verify a token; sets the cookie (web) and returns the JWT (native). */
  verify: (token: string) =>
    apiClient
      .get<VerifyResult>('/attendee/verify', { params: { token } })
      .then((r) => {
        // Persist the bearer token for native; no-op on web (no persistor).
        if (r.data?.token) setToken(r.data.token)
        return r.data
      }),

  /** Current attendee session (or { authenticated: false }). */
  me: () => apiClient.get<AttendeeMe>('/attendee/me').then((r) => r.data),

  logout: () =>
    apiClient
      .post('/attendee/logout')
      .then((r) => r.data)
      .finally(() => clearToken()),
}
